import React, { useState, useEffect, useRef } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, brandedLogoUrl } from "@/lib/api";
import Logo from "@/components/Logo";
import { MfaSettingsCard } from "@/components/settings/MfaSettingsCard";
import { DataExportCard } from "@/components/settings/DataExportCard";
import { BillingCard } from "@/components/settings/BillingCard";
import { UsageMeterCard } from "@/components/settings/UsageMeterCard";
import { Save, Palette, Check, Upload, X, ImageIcon } from "lucide-react";

export default function TenantSettings() {
  const { tenant, loading, error, update, reload } = useTenant();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (tenant) {
      setForm({
        display_name: tenant.display_name,
        branding: { ...tenant.branding },
      });
    }
  }, [tenant]);

  if (!user || !["admin", "owner"].includes(user.role)) {
    return (
      <div className="p-8">
        <div className="fop-surface p-8 text-center text-[var(--fop-text-2)] rounded-lg">
          Company settings are restricted to admins.
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-8">Loading company settings…</div>;
  if (error || !tenant) return (
    <div className="p-8" role="alert">
      <p>{error || "Company settings are unavailable."}</p>
      <button className="btn btn-primary mt-3" onClick={reload}>Retry</button>
    </div>
  );
  if (!form) return <div className="p-8">Loading…</div>;

  const setBranding = (k, v) => setForm({ ...form, branding: { ...form.branding, [k]: v } });

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await update(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSaveError(typeof detail === "string" ? detail : "Unable to save company settings.");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="tenant-settings-page">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-text-3)] mb-2">
          Company Configuration
        </div>
        <h1 className="font-display text-4xl text-[var(--fop-text)]">White-label branding</h1>
        <p className="text-[var(--fop-text-2)] mt-2">
          Every visible surface adapts to your colors, logo, and language. Changes apply instantly across the platform.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="space-y-4">
          <Card title="Identity">
            <Field label="Display name">
              <input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                data-testid="tenant-display-name"
              />
            </Field>
            <Field label="Tagline">
              <input
                value={form.branding.tagline || ""}
                onChange={(e) => setBranding("tagline", e.target.value)}
                data-testid="tenant-tagline"
              />
            </Field>
            <Field label="Logo">
              <LogoDropZone
                logoUrl={form.branding.logo_url}
                onUploaded={async (newUrl) => {
                  setBranding("logo_url", newUrl);
                  await reload();
                }}
                onRemoved={async () => {
                  setBranding("logo_url", null);
                  await reload();
                }}
              />
            </Field>
          </Card>

          <Card title="Colors">
            <div className="grid grid-cols-3 gap-3">
              <Color label="Primary" value={form.branding.primary_color} onChange={(v) => setBranding("primary_color", v)} testid="color-primary" />
              <Color label="Accent" value={form.branding.accent_color} onChange={(v) => setBranding("accent_color", v)} testid="color-accent" />
              <Color label="Secondary" value={form.branding.secondary_color} onChange={(v) => setBranding("secondary_color", v)} testid="color-secondary" />
            </div>
            <div className="mt-3 text-[11px] text-[var(--fop-text-3)] font-mono">
              Primary drives buttons, CTAs, pin highlights, and active states.
            </div>
          </Card>

          <Card title="Footer">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.branding.powered_by_forgedops}
                onChange={(e) => setBranding("powered_by_forgedops", e.target.checked)}
                className="w-auto"
                data-testid="tenant-powered-by"
              />
              <span className="text-sm text-[var(--fop-text)]">Show "Powered by ForgedOps™" in app footer</span>
            </label>
            <Field label="Custom footer text">
              <input
                value={form.branding.footer_text || ""}
                onChange={(e) => setBranding("footer_text", e.target.value)}
                placeholder="Powered by ForgedOps™"
                data-testid="tenant-footer-text"
              />
            </Field>
          </Card>

          <MfaSettingsCard />

          <UsageMeterCard />

          <BillingCard />

          <DataExportCard />

          {saveError && <p role="alert" className="text-red-300">{saveError}</p>}
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="tenant-save">
            {saved ? <Check size={14} /> : <Save size={14} />}
            {saving ? "Saving…" : saved ? "Saved" : "Save changes"}
          </button>
        </div>

        {/* Live preview */}
        <div className="fop-surface-2 rounded-lg p-5 self-start" data-testid="branding-preview">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-3">
            <Palette size={11} className="inline mr-1.5" /> Live Preview
          </div>
          <div className="space-y-3">
            <div className="border border-[var(--fop-border)] rounded-md p-3 bg-[var(--fop-surface)] flex items-center gap-3">
              {form.branding.logo_url && (
                <img
                  src={brandedLogoUrl(form.branding.logo_url)}
                  alt=""
                  className="h-7 w-auto object-contain"
                  data-testid="branding-preview-logo"
                />
              )}
              <Logo size="lg" />
            </div>
            <button className="btn btn-primary w-full" type="button">Primary CTA</button>
            <div className="flex gap-2">
              <span className="pill" style={{ color: form.branding.primary_color, background: `${form.branding.primary_color}20`, borderColor: `${form.branding.primary_color}50` }}>
                <span className="pill-dot" /> Active
              </span>
              <span className="pill" style={{ color: form.branding.accent_color, background: `${form.branding.accent_color}20`, borderColor: `${form.branding.accent_color}50` }}>
                Accent
              </span>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-4)] text-center pt-2">
              {form.branding.powered_by_forgedops ? (form.branding.footer_text || "Powered by ForgedOps™") : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="fop-surface-2 rounded-lg p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-[var(--fop-text-2)] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function Color({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-xs text-[var(--fop-text-2)] mb-1.5">{label}</div>
      <div className="flex items-center gap-2 fop-surface px-2 py-1.5 rounded border border-[var(--fop-border-2)]">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded border-0 p-0 cursor-pointer"
          style={{ background: "none" }}
          data-testid={testid}
        />
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 font-mono text-xs bg-transparent border-0 p-0"
          style={{ boxShadow: "none" }}
        />
      </div>
    </div>
  );
}

function LogoDropZone({ logoUrl, onUploaded, onRemoved }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [hover, setHover] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tenant/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onUploaded(data.logo_url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/tenant/logo");
      await onRemoved();
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const previewSrc = brandedLogoUrl(logoUrl);

  return (
    <div data-testid="tenant-logo-dropzone">
      {logoUrl ? (
        <div className="flex items-center gap-3 fop-surface border border-[var(--fop-border-2)] rounded p-3">
          <div className="w-20 h-12 bg-[var(--fop-surface-2)] rounded flex items-center justify-center overflow-hidden">
            <img src={previewSrc} alt="Tenant logo" className="max-w-full max-h-full object-contain" data-testid="tenant-logo-preview" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono text-[var(--fop-text-2)] truncate">{logoUrl}</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] mt-0.5">
              Appears on Coordination Report cover + Shell branding
            </div>
          </div>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="btn btn-ghost text-xs" data-testid="tenant-logo-replace">
            <Upload size={12} /> Replace
          </button>
          <button type="button" onClick={remove} disabled={busy} className="btn btn-ghost text-xs text-[var(--st-escalated)]" data-testid="tenant-logo-remove">
            <X size={12} />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`fop-surface border-2 border-dashed rounded p-5 text-center cursor-pointer transition-colors ${
            hover ? "border-[var(--fop-primary)] bg-[var(--fop-primary-soft)]" : "border-[var(--fop-border-2)]"
          }`}
          data-testid="tenant-logo-dropzone-empty"
        >
          <ImageIcon size={22} className="mx-auto text-[var(--fop-text-3)] mb-2" />
          <div className="text-sm text-[var(--fop-text)]">
            {busy ? "Uploading…" : "Drop a logo here, or click to choose"}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] mt-1.5">
            PNG, JPG, WebP · Max 15MB · Tenant-scoped
          </div>
        </div>
      )}
      {error && <div className="text-xs text-[var(--st-escalated)] mt-2" data-testid="tenant-logo-error">{error}</div>}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
        data-testid="tenant-logo-input"
      />
    </div>
  );
}
